import { EventBus }  from '../core/EventBus.js';
import { GameState } from '../core/GameState.js';

import { FleetPanel }     from './panels/FleetPanel.js';
import { ContractPanel }  from './panels/ContractPanel.js';
import { MarketPanel }    from './panels/MarketPanel.js';
import { ResearchPanel }  from './panels/ResearchPanel.js';
import { FinancePanel }   from './panels/FinancePanel.js';
import { RoutesPanel }    from './panels/RoutesPanel.js';
import { WarehousesPanel } from './panels/WarehousesPanel.js';
import { PortPanel }      from './panels/PortPanel.js';

import { WarehouseModal }     from './modals/WarehouseModal.js';
import { StaffModal }         from './modals/StaffModal.js';
import { RouteEditorModal }   from './modals/RouteEditorModal.js';
import { FeedbackModal }     from './modals/FeedbackModal.js';
import { GameMenuModal }     from './modals/GameMenuModal.js';

import { isFeatureUnlocked }  from '../config/research.js';
import { formatMoney }        from '../utils/formatting.js';
import { calculateRoute }     from '../utils/seaRoutes.js';
import { previewEtaText }     from './panels/etaInfo.js';
import { showConfirm }        from './components/dialogs.js';
import { serverIntent }      from '../core/serverIntent.js';

/**
 * Orchestrates tab switching in the bottom panel,
 * the side (port) panel, and global modals.
 */
export class UIManager {
  constructor({ navigationSystem, tradeSystem, fuelSystem, contractSystem, researchSystem, warehouseSystem, staffSystem, saveManager }) {
    this.navSystem       = navigationSystem;
    this.tradeSystem     = tradeSystem;
    this.fuelSystem      = fuelSystem;
    this.contractSystem  = contractSystem;
    this.researchSystem  = researchSystem;
    this.warehouseSystem = warehouseSystem;
    this.staffSystem     = staffSystem;

    // Bottom-panel tabs
    this.panels = {
      fleet:      new FleetPanel(this),
      contracts:  new ContractPanel(this),
      market:     new MarketPanel(this),
      research:   new ResearchPanel(this),
      routes:     new RoutesPanel(this),
      warehouses: new WarehousesPanel(this),
      finance:    new FinancePanel(this)
    };
    this.activeTab = 'fleet';

    // Side panel (port details)
    this.portPanel    = new PortPanel(this);
    this.selectedPort = null;
    this.selectedShip = null;

    // Feature modals (instantiate once, they listen via EventBus)
    this.gameMenuModal   = new GameMenuModal(saveManager);
    this.warehouseModal  = new WarehouseModal({ warehouseSystem });
    this.staffModal      = new StaffModal({ staffSystem });
    this.routeEditorModal = new RouteEditorModal();
    this.feedbackModal    = new FeedbackModal();
  }

  init() {
    this._bindTabs();
    this._bindEvents();
    this._bindPanelResize();
    this._bindSidePanelResize();
    this.showTab('fleet');
  }

  // ---------- bottom tabs ----------
  _bindTabs() {
    document.querySelectorAll('.panel-tabs button[data-panel]').forEach(btn => {
      btn.addEventListener('click', () => this.showTab(btn.dataset.panel));
    });
  }

  _bindPanelResize() {
    const handle = document.getElementById('panel-resize-handle');
    const panel = document.getElementById('bottom-panel');
    if (!handle || !panel) return;

    let startY, startH;
    const onMove = (e) => {
      const dy = startY - (e.clientY ?? e.touches[0].clientY);
      const newH = Math.min(Math.max(startH + dy, 120), window.innerHeight * 0.8);
      panel.style.height = newH + 'px';
      window.dispatchEvent(new Event('resize'));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    const onDown = (e) => {
      e.preventDefault();
      startY = e.clientY ?? e.touches[0].clientY;
      startH = panel.offsetHeight;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onUp);
    };
    handle.addEventListener('mousedown', onDown);
    handle.addEventListener('touchstart', onDown, { passive: false });
  }

  _bindSidePanelResize() {
    const handle = document.getElementById('side-resize-handle');
    const panel = document.getElementById('side-panel');
    if (!handle || !panel) return;

    let startX, startW;
    const onMove = (e) => {
      const dx = startX - (e.clientX ?? e.touches[0].clientX);
      const newW = Math.min(Math.max(startW + dx, 280), window.innerWidth * 0.5);
      panel.style.width = newW + 'px';
      window.dispatchEvent(new Event('resize'));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    const onDown = (e) => {
      e.preventDefault();
      startX = e.clientX ?? e.touches[0].clientX;
      startW = panel.offsetWidth;
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onUp);
    };
    handle.addEventListener('mousedown', onDown);
    handle.addEventListener('touchstart', onDown, { passive: false });
  }

  showTab(name) {
    const panel = this.panels[name];
    if (!panel) return;
    this.activeTab = name;

    // Highlight active tab button
    document.querySelectorAll('.panel-tabs button').forEach(b => {
      b.classList.toggle('active', b.dataset.panel === name);
    });

    // Render panel content
    const content = document.querySelector('.panel-content');
    content.innerHTML = '';
    content.classList.remove('panel-content--market');
    panel.render();
  }

  // ---------- side panel (port) ----------
  _bindEvents() {
    EventBus.on('ui:portSelected', ({ port, tab, highlightCrew }) => {
      // If in route selection mode, select this port as destination
      if (this._routeSelectionShip) {
        if (window.innerWidth <= 768) {
          this._showMobileRouteConfirm(port);
        } else {
          this._selectRouteDestination(port);
        }
        return;
      }
      // Auto-select a ship at this port if current selection isn't here
      if (!this.selectedShip || this.selectedShip.currentPortId !== port.id) {
        const dockedShips = GameState.getAllShips()
          .filter(s => s.status === 'docked' && s.currentPortId === port.id);
        if (dockedShips.length > 0) {
          this.selectedShip = dockedShips[0]; // first docked ship at port
        }
      }
      this.openPortPanel(port, tab, highlightCrew);
    });
    EventBus.on('ui:shipSelected', ({ ship }) => this.onShipSelected(ship));

    // Re-render active tab on data changes
    // A panel may define shouldRerender(event, data) to skip events that
    // can't change what it shows — the Market table (~18k cells) takes the
    // better part of a second to rebuild, and ship:fuelChanged /
    // conditionChanged alone fire once a second per sailing ship.
    const rerender = (event, data) => {
      const panel = this.panels[this.activeTab];
      if (!panel) return;
      if (panel.shouldRerender && !panel.shouldRerender(event, data)) return;
      panel.render();
    };
    EventBus.on('game:loaded',         (d) => rerender('game:loaded', d));
    EventBus.on('contracts:updated',   (d) => rerender('contracts:updated', d));
    EventBus.on('contracts:updated',   () => { if (this.selectedPort) this.portPanel.refreshContracts(); });
    const refreshServices = (d) => { if (this.selectedPort) this.portPanel.refreshServices(d?.shipId || d?.ship?.id); };
    EventBus.on('ship:maintenanceChanged', refreshServices);
    EventBus.on('ship:fuelChanged',        refreshServices);
    EventBus.on('ship:conditionChanged',   refreshServices);
    EventBus.on('market:updated',      (d) => rerender('market:updated', d));
    EventBus.on('ship:arrived',        (d) => rerender('ship:arrived', d));
    EventBus.on('ship:departed',       (d) => rerender('ship:departed', d));
    EventBus.on('ship:purchased',      (d) => rerender('ship:purchased', d));
    EventBus.on('ship:sold',           (d) => rerender('ship:sold', d));
    EventBus.on('cargo:loaded',        (d) => rerender('cargo:loaded', d));
    EventBus.on('cargo:unloaded',      (d) => rerender('cargo:unloaded', d));
    EventBus.on('ship:fuelChanged',      (d) => rerender('ship:fuelChanged', d));
    EventBus.on('ship:conditionChanged', (d) => rerender('ship:conditionChanged', d));
    EventBus.on('ship:handlingChanged',  (d) => rerender('ship:handlingChanged', d));
    EventBus.on('ship:maintenanceChanged', (d) => rerender('ship:maintenanceChanged', d));
    EventBus.on('ship:renamed',          (d) => rerender('ship:renamed', d));
    EventBus.on('staff:crewHired',       (d) => rerender('staff:crewHired', d));
    EventBus.on('staff:crewFired',       (d) => rerender('staff:crewFired', d));
    EventBus.on('staff:analystHired',    (d) => rerender('staff:analystHired', d));
    EventBus.on('staff:analystFired',    (d) => rerender('staff:analystFired', d));
    EventBus.on('money:changed',       (d) => rerender('money:changed', d));
    EventBus.on('research:started',    (d) => rerender('research:started', d));
    EventBus.on('research:completed',  (d) => rerender('research:completed', d));
    EventBus.on('research:cancelled',  (d) => rerender('research:cancelled', d));
    EventBus.on('permit:started',      (d) => rerender('permit:started', d));
    EventBus.on('permit:completed',    (d) => rerender('permit:completed', d));
    EventBus.on('permit:cancelled',    (d) => rerender('permit:cancelled', d));
    EventBus.on('permit:bribeFailed',  (d) => rerender('permit:bribeFailed', d));
    EventBus.on('route:saved',         (d) => rerender('route:saved', d));
    EventBus.on('route:loopCompleted', (d) => rerender('route:loopCompleted', d));
    EventBus.on('route:autoPaused',    (d) => rerender('route:autoPaused', d));
    EventBus.on('warehouse:built',            (d) => rerender('warehouse:built', d));
    EventBus.on('warehouse:upgraded',         (d) => rerender('warehouse:upgraded', d));
    EventBus.on('warehouse:cargoTransferred', (d) => rerender('warehouse:cargoTransferred', d));

    // Show/hide Routes tab based on research
    const checkRoutesTab = () => this._updateRoutesTabVisibility();
    EventBus.on('research:completed', checkRoutesTab);
    EventBus.on('game:started',       checkRoutesTab);
    EventBus.on('game:loaded',        checkRoutesTab);

    // Show/hide Warehouses tab based on research
    const checkWarehousesTab = () => this._updateWarehousesTabVisibility();
    EventBus.on('research:completed', checkWarehousesTab);
    EventBus.on('game:started',       checkWarehousesTab);
    EventBus.on('game:loaded',        checkWarehousesTab);

    // Refresh port panel when ships arrive/depart
    const refreshPortPanel = ({ ship, port }) => {
      if (this.selectedPort) {
        // Refresh if ship arrived at or departed from the selected port
        const affectedPortId = port?.id || ship?.currentPortId;
        if (affectedPortId === this.selectedPort.id) {
          this.portPanel.render(this.selectedPort);
        }
      }
    };
    EventBus.on('ship:arrived',  (data) => {
      // Auto-select arriving ship
      this.selectedShip = data.ship;
      refreshPortPanel(data);
    });
    EventBus.on('ship:departed', refreshPortPanel);

    // Market analyst hire/fire changes what the CURRENTLY OPEN port panel's
    // crew-hire section shows (e.g. "Already have an analyst" disabling
    // the Hire button) regardless of which port that panel happens to be
    // showing — unlike refreshPortPanel() above, not gated to one specific
    // port.id, since hiring at port A affects the Hire button at port B too.
    EventBus.on('staff:analystHired', () => {
      if (this.selectedPort) this.portPanel.render(this.selectedPort);
    });
    EventBus.on('staff:analystFired', () => {
      if (this.selectedPort) this.portPanel.render(this.selectedPort);
    });
  }

  // ---------- route selection mode ----------
  startRouteSelection(ship) {
    this._routeSelectionShip = ship;

    // Close port panel so the map is fully visible
    this.closePortPanel();

    // Show banner with cancel option
    this._showRouteSelectionBanner(ship);
    document.body.classList.add('route-selection-mode');
    EventBus.emit('map:dimPorts', { shipType: ship.type, ship });

    // Escape key cancels
    this._routeSelectionEscHandler = (e) => {
      if (e.key === 'Escape') this.cancelRouteSelection();
    };
    document.addEventListener('keydown', this._routeSelectionEscHandler);

    // Let Leaflet recalculate after side panel layout change
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
  }

  _showRouteSelectionBanner(ship) {
    this._removeRouteSelectionBanner();
    const banner = document.createElement('div');
    banner.id = 'route-selection-banner';
    banner.innerHTML = `
      <span>Select destination for <strong>${ship.name}</strong></span>
      <button class="btn btn-sm btn-danger" id="cancel-route-selection">Cancel (Esc)</button>`;
    document.body.appendChild(banner);
    document.getElementById('cancel-route-selection')
      .addEventListener('click', () => this.cancelRouteSelection());
  }

  _removeRouteSelectionBanner() {
    const existing = document.getElementById('route-selection-banner');
    if (existing) existing.remove();
  }

  _selectRouteDestination(port) {
    const ship = this._routeSelectionShip;
    if (!ship || port.id === ship.currentPortId) {
      this.cancelRouteSelection();
      return;
    }

    // Pre-departure fuel check — warn player if insufficient
    const routeInfo = calculateRoute(ship.position, port.position);
    const fuelCheck = ship.hasFuelForVoyage(routeInfo.distance);

    if (!fuelCheck.enough) {
      // Check if engineer will auto-refuel (senior/expert at port with fuel)
      const engineer = ship.crew.engineer ? GameState.crewMembers.get(ship.crew.engineer) : null;
      const originPort = ship.currentPortId ? GameState.getPort(ship.currentPortId) : null;
      const willAutoRefuel = engineer
        && (engineer.skillTier === 'senior' || engineer.skillTier === 'expert')
        && originPort?.services?.fuel
        && GameState.player.canAfford(Math.round((fuelCheck.fuelNeeded - ship.fuel) * originPort.fuelPrice));

      if (!willAutoRefuel) {
        // Show warning — ship will run out of fuel
        const pct = Math.round((fuelCheck.fuelAvailable / fuelCheck.fuelNeeded) * 100);
        showConfirm(
          `${ship.name} does not have enough fuel for this voyage!<br><br>` +
          `Fuel needed: <strong>${fuelCheck.fuelNeeded} t</strong><br>` +
          `Fuel available: <strong>${fuelCheck.fuelAvailable} t</strong> (${pct}%)<br><br>` +
          `The ship will run out of fuel and become stranded.`,
          { title: 'Insufficient Fuel', confirmLabel: 'Depart Anyway' }
        ).then(ok => {
          if (!ok) return;
          this._executeDepart(ship, port);
        });
        this.cancelRouteSelection();
        return;
      }
    }

    this._executeDepart(ship, port);
  }

  async _executeDepart(ship, port) {
    this.cancelRouteSelection();
    this.panels.fleet.render();
    // Send depart intent to server
    const result = await serverIntent('ship/depart', {
      shipId: ship.id,
      destinationPortId: port.id
    });
    if (result.success) {
      EventBus.emit('map:syncRequested');
      EventBus.emit('ui:notification', {
        message: `${ship.name} departing to ${port.name}`,
        type: 'success'
      });
    }
  }

  /** Mobile: show port info popup with Set Route button instead of immediate selection */
  _showMobileRouteConfirm(port) {
    const ship = this._routeSelectionShip;
    if (!ship || port.id === ship.currentPortId) return;

    // Remove any previous confirm popup
    this._removeMobileRouteConfirm();

    // Calculate route distance for info
    const routeInfo = calculateRoute(ship.position, port.position);
    const distNm = Math.round(routeInfo.distance);

    const fuelCheck = ship.hasFuelForVoyage(routeInfo.distance);
    const fuelWarn = !fuelCheck.enough
      ? `<div class="route-confirm-fuel-warn">Not enough fuel! ${fuelCheck.fuelAvailable}/${fuelCheck.fuelNeeded} t</div>`
      : '';

    const eta = previewEtaText(ship, routeInfo.distance);
    const etaLine = eta ? `ETA: ${eta}<br>` : '';

    const popup = document.createElement('div');
    popup.id = 'route-confirm-popup';
    popup.innerHTML = `
      <div class="route-confirm-header"><strong>${port.name}</strong></div>
      <div class="route-confirm-info">
        ${port.country} &mdash; ${port.size}<br>
        Distance: ${distNm.toLocaleString()} nm<br>
        ${etaLine}
        Fuel: $${port.fuelPrice}/t &nbsp; Dock: ${formatMoney(port.dockingFee)}
        ${fuelWarn}
      </div>
      <button class="btn btn-primary btn-sm" id="route-confirm-btn">Set Route</button>`;
    document.body.appendChild(popup);

    document.getElementById('route-confirm-btn')
      .addEventListener('click', () => {
        this._removeMobileRouteConfirm();
        this._selectRouteDestination(port);
      });
  }

  _removeMobileRouteConfirm() {
    const el = document.getElementById('route-confirm-popup');
    if (el) el.remove();
  }

  cancelRouteSelection() {
    this._routeSelectionShip = null;
    this._removeMobileRouteConfirm();
    document.body.classList.remove('route-selection-mode');
    this._removeRouteSelectionBanner();
    EventBus.emit('map:resetDim');
    if (this._routeSelectionEscHandler) {
      document.removeEventListener('keydown', this._routeSelectionEscHandler);
      this._routeSelectionEscHandler = null;
    }
  }

  _updateRoutesTabVisibility() {
    const btn = document.getElementById('routes-tab-btn');
    if (!btn) return;
    if (isFeatureUnlocked('trade_routes')) {
      btn.classList.remove('hidden');
    } else {
      btn.classList.add('hidden');
    }
  }

  _updateWarehousesTabVisibility() {
    const btn = document.getElementById('warehouses-tab-btn');
    if (!btn) return;
    if (isFeatureUnlocked('warehouses')) {
      btn.classList.remove('hidden');
    } else {
      btn.classList.add('hidden');
    }
  }

  openPortPanel(port, defaultTab, highlightCrew) {
    this.selectedPort = port;
    const side = document.getElementById('side-panel');
    side.classList.remove('collapsed');
    document.getElementById('side-resize-handle')?.classList.remove('hidden');
    this.portPanel.render(port, defaultTab, highlightCrew);
    this._refreshMarketPins();
  }

  closePortPanel() {
    this.selectedPort = null;
    document.getElementById('side-panel').classList.add('collapsed');
    document.getElementById('side-resize-handle')?.classList.add('hidden');
    this._refreshMarketPins();
  }

  /** The Market tab pins the selected port's column — refresh it when that changes. */
  _refreshMarketPins() {
    if (this.activeTab === 'market') this.panels.market.render();
  }

  onShipSelected(ship) {
    this.selectedShip = ship;

    // If fleet tab is active, highlight the ship card
    if (this.activeTab === 'fleet') {
      setTimeout(() => {
        document.querySelectorAll('.ship-card.selected').forEach(el => el.classList.remove('selected'));
        const el = document.querySelector(`[data-ship-id="${ship.id}"]`);
        if (el) {
          el.classList.add('selected');
          el.scrollIntoView({ block: 'nearest' });
        }
      }, 50);
    }

    // If market tab is active, update ship selector
    if (this.activeTab === 'market') {
      this.panels.market.selectShip(ship.id);
    }
  }

}
